import React, {
	createContext,
	useContext,
	Component,
	useState,
	useMemo,
	useCallback,
	type ReactNode,
} from 'react';
import noop from 'lodash/noop';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { FieldKey, FieldValue } from '../../common/types.tsx';

export type IccContextType = {
	initialSummary: string;
	initialCardTypeId: number | string | null;
	initialFieldValues: Map<FieldKey, FieldValue>;
	initialProjectId: number | null;
	onChangeSummary: (summary: string) => void;
	onChangeCardType: (cardTypeId: number | string | null) => void;
	onChangeFieldValue: (fieldKey: FieldKey, newFieldValue: FieldValue) => void;
	onChangeProject: (projectId: number) => void;
	onCancel: () => void;
	onCreate: () => void;
};

type Props = {
	children: ReactNode;
};

const IccContext = createContext<IccContextType>({
	initialSummary: '',
	initialCardTypeId: null,
	initialFieldValues: new Map(),
	initialProjectId: null,
	onChangeSummary: noop,
	onChangeCardType: noop,
	onChangeFieldValue: noop,
	onChangeProject: noop,
	onCancel: noop,
	onCreate: noop,
});

const useIccContext = () => useContext(IccContext);

type State = {
	initialSummary: string;
	initialCardTypeId: number | string | null;
	initialProjectId: number | null;
	initialFieldValues: Map<FieldKey, FieldValue>;
};

// eslint-disable-next-line jira/react/no-class-components
class IccProviderOld extends Component<Props, State> {
	state = {
		initialSummary: '',
		initialCardTypeId: null,
		initialFieldValues: new Map(),
		initialProjectId: null,
	};

	onChangeSummary = (summary: string) => {
		this.setState({ initialSummary: summary });
	};

	onChangeProject = (projectId: number) => {
		this.setState({ initialProjectId: projectId });
	};

	onChangeCardType = (cardTypeId: number | string | null) => {
		this.setState({ initialCardTypeId: cardTypeId });
	};

	onChangeFieldValue = (fieldKey: FieldKey, fieldValue: FieldValue) => {
		this.setState(({ initialFieldValues }) => ({
			initialFieldValues: initialFieldValues.set(fieldKey, fieldValue),
		}));
	};

	onCancel = () => {
		this.setState({ initialSummary: '', initialCardTypeId: null });
	};

	onCreate = () => {
		this.setState({ initialSummary: '' });
	};

	render() {
		return (
			<IccContext.Provider
				value={{
					initialSummary: this.state.initialSummary,
					initialCardTypeId: this.state.initialCardTypeId,
					initialFieldValues: this.state.initialFieldValues,
					initialProjectId: this.state.initialProjectId,
					onChangeCardType: this.onChangeCardType,
					onChangeSummary: this.onChangeSummary,
					onChangeFieldValue: this.onChangeFieldValue,
					onChangeProject: this.onChangeProject,
					onCancel: this.onCancel,
					onCreate: this.onCreate,
				}}
			>
				{this.props.children}
			</IccContext.Provider>
		);
	}
}

const IccProviderNew = (props: Props) => {
	const [state, setState] = useState<State>({
		initialSummary: '',
		initialCardTypeId: null,
		initialFieldValues: new Map(),
		initialProjectId: null,
	});

	const onChangeSummary = useCallback((summary: string) => {
		setState((prevState) => ({ ...prevState, initialSummary: summary }));
	}, []);

	const onChangeProject = useCallback((projectId: number) => {
		setState((prevState) => ({ ...prevState, initialProjectId: projectId }));
	}, []);

	const onChangeCardType = useCallback((cardTypeId: number | string | null) => {
		setState((prevState) => ({ ...prevState, initialCardTypeId: cardTypeId }));
	}, []);

	const onChangeFieldValue = useCallback((fieldKey: FieldKey, fieldValue: FieldValue) => {
		setState(({ initialFieldValues, ...prevState }) => ({
			...prevState,
			initialFieldValues: initialFieldValues.set(fieldKey, fieldValue),
		}));
	}, []);

	const onCancel = useCallback(() => {
		setState((prevState) => ({ ...prevState, initialSummary: '', initialCardTypeId: null }));
	}, []);

	const onCreate = useCallback(() => {
		setState((prevState) => ({ ...prevState, initialSummary: '' }));
	}, []);

	const values = useMemo(
		() => ({
			initialSummary: state.initialSummary,
			initialCardTypeId: state.initialCardTypeId,
			initialFieldValues: state.initialFieldValues,
			initialProjectId: state.initialProjectId,
			onChangeCardType,
			onChangeSummary,
			onChangeFieldValue,
			onChangeProject,
			onCancel,
			onCreate,
		}),
		[
			state,
			onChangeCardType,
			onChangeSummary,
			onChangeFieldValue,
			onChangeProject,
			onCancel,
			onCreate,
		],
	);

	return <IccContext.Provider value={values}>{props.children}</IccContext.Provider>;
};

const IccProvider = componentWithFG(
	'board_rendering_optimisations',
	IccProviderNew,
	IccProviderOld,
);

export { IccContext, IccProvider, useIccContext };
